import type React from 'react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import CookiesPolicyModal from '../components/Modals/CookiesPolicyModal';
import LegalNoticesModal from '../components/Modals/LegalNoticesModal';
import ModernSlaveryModal from '../components/Modals/ModernSlaveryModal';
import FAQModal from '../pages/AdvancedSearch/components/FAQModal';
import { aosphereURL, contactUsEmail, publicURL } from '../util/constants';
import { captureEventAnonymous } from '../util/posthog';

interface AuthLayoutProps {
  children: ReactNode;
}

const trackLoginPageLink = (text: string, href: string) => {
  void captureEventAnonymous('login_page_links', { text, href });
};

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const currentYear = new Date().getFullYear();
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showCookiesPolicyModal, setShowCookiesPolicyModal] = useState(false);
  const [showLegalNoticesModal, setShowLegalNoticesModal] = useState(false);
  const [showModernSlaveryModal, setShowModernSlaveryModal] = useState(false);

  return (
    <div className="container-fluid d-flex justify-content-center align-items-center min-vh-100">
      <div className="ao_loginContainer">
        <div className="ao_loginLogo">
          <img
            className="img-fluid"
            src="assets/aoslogin_brand_update.png"
            width="450"
            alt="Logo"
          />
        </div>

        <div className="ao_loginBox" style={{ top: '15px' }}>
          {' '}
          {children}{' '}
        </div>
        <div className="ao_loginCnt">
          <p>
            <b>Login to access your organisation's subscriptions.</b>{' '}
          </p>
          <p>
            View our{' '}
            <Link
              to={`${aosphereURL}/privacy-statement/`}
              target="_blank"
              onClick={() => trackLoginPageLink('Privacy Statement', `${aosphereURL}/privacy-statement/`)}
            >
              Privacy Statement
            </Link>{' '}
            and{' '}
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                trackLoginPageLink('Cookies Policy', '#');
                setShowCookiesPolicyModal(true);
              }}
            >
              Cookies Policy
            </Link>
            , which includes information on cookies used on this website and how
            to manage them. By logging in, you agree to the use of cookies in
            accordance with our Privacy Statement and Cookies Policy.
          </p>
          <p>
            Review the{' '}
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                trackLoginPageLink('legal notices', `${publicURL}/docs/LegalNotices.pdf`);
                setShowLegalNoticesModal(true);
              }}
            >
              legal notices
            </Link>{' '}
            for aosphere and{' '}
            <Link
              to={`${aosphereURL}/`}
              onClick={() => trackLoginPageLink('https://www.aosphere.com', `${aosphereURL}/`)}
            >
              http://www.aosphere.com
            </Link>
            .
          </p>
          <p>
            Learn about our new{' '}
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                trackLoginPageLink('Intelligent Search', '#');
                setShowFAQModal(true);
              }}
            >
              Intelligent Search
            </Link>{' '}
            beta feature and how it works. By using Intelligent Search, you
            acknowledge that it is a beta feature with evolving capabilities,
            and that provisional answers should always be verified against
            source documentation.
          </p>
          <p>
            For assistance, please{' '}
            <Link
              to={`mailto:${contactUsEmail}`}
              target="_self"
              onClick={() => trackLoginPageLink('contact us', `mailto:${contactUsEmail}`)}
            >
              contact us
            </Link>
            .
          </p>
        </div>

        <div className="ao_loginFooter">
          <div className="d-flex ao_footerLinks">
            <div className="col">
              &copy; aosphere {currentYear} <span className="pipeline">|</span>
              <Link
                to={`${aosphereURL}/privacy-statement/`}
                target="_blank"
                onClick={() => trackLoginPageLink('Privacy Statement', `${aosphereURL}/privacy-statement/`)}
              >
                Privacy Statement
              </Link>{' '}
              <span className="pipeline">|</span>
              <Link
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  trackLoginPageLink('Cookies Policy', `${publicURL}/docs/CookiePolicy.pdf`);
                  setShowCookiesPolicyModal(true);
                }}
              >
                Cookies Policy
              </Link>{' '}
              <span className="pipeline">|</span>
              <Link
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  trackLoginPageLink('Legal Notices', `${publicURL}/docs/LegalNotices.pdf`);
                  setShowLegalNoticesModal(true);
                }}
              >
                Legal Notices
              </Link>{' '}
              <span className="pipeline">|</span>
              <Link
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  trackLoginPageLink('Modern Slavery', `${publicURL}/docs/ModernSlavery.pdf`);
                  setShowModernSlaveryModal(true);
                }}
              >
                Modern Slavery
              </Link>
              <br />
              <p className="greyFont semiboldNone form-text mb-0">
                aosphere Limited registered in England and Wales with registered
                number 15371365 and registered office at The Steward Building,
                12 Steward Street, London, E1 6FQ, United Kingdom.
              </p>
            </div>
            <div className="ms-auto">
              <Link
                to="https://www.linkedin.com/company/aosphere"
                target="_blank"
                onClick={() => trackLoginPageLink('LinkedIn', 'https://www.linkedin.com/company/aosphere')}
              >
                <img
                  src="assets/images/linkedIn.svg"
                  width="22"
                  alt="LinkedIn"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <FAQModal show={showFAQModal} onHide={() => setShowFAQModal(false)} />
      <CookiesPolicyModal
        show={showCookiesPolicyModal}
        onHide={() => setShowCookiesPolicyModal(false)}
      />
      <LegalNoticesModal
        show={showLegalNoticesModal}
        onHide={() => setShowLegalNoticesModal(false)}
      />
      <ModernSlaveryModal
        show={showModernSlaveryModal}
        onHide={() => setShowModernSlaveryModal(false)}
      />
    </div>
  );
};

export default AuthLayout;
